import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chantiers, getClientById, getChefEquipe, getAllChantierImages } from '../utils/mockData';
import { useAuth } from '../utils/auth';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Button } from '../components/ui/Button';
import { ImageGallery } from '../components/ImageGallery';
import { TaskValidation } from '../components/TaskValidation';
import { Card } from '../components/ui/Card';
import { MapPin, Calendar, ArrowLeft, Users, UserCheck, AlertCircle, Edit, CheckCircle, Upload, Star, FileText, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';
import { WorkType } from '../utils/types';
const workTypeLabels: Record<WorkType, string> = {
  signaletique_interieure: 'Signalétique intérieure',
  signaletique_exterieure: 'Signalétique extérieure',
  marquage_sol: 'Marquage au sol',
  revetement: 'Revêtement / Résine',
  peinture_decorative: 'Peinture décorative',
  mobilier: 'Installation mobilier',
  amenagement_commercial: 'Aménagement commercial',
  maintenance_preventive: 'Maintenance préventive',
  intervention_corrective: 'Intervention corrective',
  renovation_partielle: 'Rénovation partielle'
};
const docTypeLabels: Record<string, string> = {
  devis: 'Devis',
  bon_commande: 'Bon de commande',
  plan: 'Plan technique',
  bon_livraison: 'Bon de livraison',
  pv_reception: 'PV de réception',
  rapport: 'Rapport',
  facture: 'Facture',
  autre: 'Autre'
};
export function ChantierDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const chantier = chantiers.find(c => c.id === id);
  const [tasks, setTasks] = useState(chantier?.tasks || []);
  if (!chantier) {
    return <div className="page-container py-20 text-center" data-id="element-245">
        <p className="text-gray-500 text-lg" data-id="element-246">Chantier non trouvé</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)} data-id="element-247">
          Retour
        </Button>
      </div>;
  }
  const client = getClientById(chantier.clientId);
  const chef = getChefEquipe(chantier.chefEquipeId);
  const canEdit = user?.role === 'admin' || user?.role === 'bureau';
  const canValidate = user?.role === 'admin' || user?.role === 'bureau' || user?.role === 'chef_equipe' && user?.id === chantier.chefEquipeId;
  const pendingCount = tasks.filter(t => t.validationStatus === 'pending').length;
  const approvedTasks = tasks.filter(t => t.validationStatus === 'approved');
  const allImages = tasks.flatMap(t => t.images);
  const handleValidate = (taskId: string, status: 'approved' | 'rejected', comment?: string) => {
    setTasks(prev => prev.map(t => t.id === taskId ? {
      ...t,
      validationStatus: status,
      validatedBy: user?.name,
      validatedAt: new Date().toISOString(),
      validationComment: comment
    } : t));
  };
  return <div className="min-h-screen bg-gray-50 pb-20 md:pb-6" data-id="element-248">
      <div className="page-container section-spacing" data-id="element-249">
        {/* Top bar */}
        <div className="flex items-center justify-between" data-id="element-250">
          <Button variant="ghost" size="sm" className="pl-0 hover:bg-transparent hover:text-[#1DBAB4]" onClick={() => navigate(-1)} leftIcon={<ArrowLeft size={16} data-id="element-252" />} data-id="element-251">
            Retour
          </Button>
          {canEdit && <Button variant="outline" size="sm" leftIcon={<Edit size={16} data-id="element-254" />} onClick={() => navigate(`/chantiers/${id}/edit`)} data-id="element-253">
              Modifier
            </Button>}
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-255">
          {/* Main Content */}
          <div className="flex-1 space-y-6" data-id="element-256">
            {/* Header */}
            <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} data-id="element-257">
              <div className="flex flex-wrap items-center gap-3 mb-2" data-id="element-258">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-259">
                  {chantier.name}
                </h1>
                <StatusBadge status={chantier.status} data-id="element-260" />
                {chantier.priority === 'urgent' && <span className="px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-200 rounded-full text-xs font-medium" data-id="element-261">
                    Urgent
                  </span>}
                {chantier.priority === 'critique' && <span className="px-2 py-0.5 bg-red-100 text-red-800 border border-red-200 rounded-full text-xs font-medium" data-id="element-262">
                    Critique
                  </span>}
              </div>
              <div className="flex items-center text-gray-500 text-sm" data-id="element-263">
                <MapPin size={16} className="mr-2 flex-shrink-0" data-id="element-264" />
                <span data-id="element-265">
                  {chantier.location}, {chantier.city}, {chantier.country}
                </span>
              </div>
            </motion.div>

            {/* Client Card */}
            {client && <Card className="p-4 bg-teal-50 border-teal-200 cursor-pointer" onClick={() => navigate(`/clients/${client.id}`)} data-id="element-266">
                <div className="flex items-center gap-3" data-id="element-267">
                  <div className="p-2 bg-teal-100 rounded-lg" data-id="element-268">
                    <Briefcase size={18} className="text-teal-700" data-id="element-269" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-270">
                    <div className="flex items-center gap-2" data-id="element-271">
                      <p className="font-semibold text-teal-900" data-id="element-272">
                        {client.raisonSociale}
                      </p>
                      {client.isStrategicAccount && <Star size={14} className="text-amber-500 fill-amber-500" data-id="element-273" />}
                    </div>
                    <p className="text-sm text-teal-700" data-id="element-274">
                      Contact : {client.contactName}
                    </p>
                  </div>
                </div>
              </Card>}

            {/* Work type & Description */}
            <Card hover={false} className="p-4 space-y-3" data-id="element-275">
              <div className="flex items-center gap-2" data-id="element-276">
                <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium" data-id="element-277">
                  {workTypeLabels[chantier.workType]}
                </span>
              </div>
              {chantier.description && <p className="text-sm text-gray-600 leading-relaxed" data-id="element-278">
                  {chantier.description}
                </p>}
            </Card>

            {/* Validation Alert */}
            {canValidate && pendingCount > 0 && <Card hover={false} className="p-4 bg-amber-50 border-amber-200" data-id="element-279">
                <div className="flex items-start gap-3" data-id="element-280">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-281" />
                  <div data-id="element-282">
                    <p className="font-medium text-amber-900" data-id="element-283">
                      {pendingCount} tâche{pendingCount > 1 ? 's' : ''} en
                      attente de validation
                    </p>
                    <p className="text-sm text-amber-700 mt-1" data-id="element-284">
                      Validez les tâches réalisées pour mettre à jour le statut
                      du chantier
                    </p>
                  </div>
                </div>
              </Card>}

            {/* Task Validation */}
            {canValidate && pendingCount > 0 && <Card className="p-4 sm:p-6" data-id="element-285">
                <TaskValidation tasks={tasks} onValidate={handleValidate} data-id="element-286" />
              </Card>}

            {/* Technicien upload */}
            {user?.role === 'technicien' && <Card className="p-4 sm:p-6" data-id="element-287">
                <h2 className="text-lg font-bold text-gray-900 mb-3" data-id="element-288">
                  Déclarer une tâche réalisée
                </h2>
                <Button leftIcon={<Upload size={18} data-id="element-290" />} data-id="element-289">
                  Ajouter une tâche avec photos
                </Button>
                <p className="text-sm text-gray-500 mt-3 flex items-start gap-2" data-id="element-291">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" data-id="element-292" />
                  <span data-id="element-293">
                    Les tâches déclarées seront soumises à validation par le
                    chef d'équipe
                  </span>
                </p>
              </Card>}

            {/* Approved Tasks */}
            {approvedTasks.length > 0 && <Card className="p-4 sm:p-6" data-id="element-294">
                <div className="flex items-center gap-2 mb-4" data-id="element-295">
                  <CheckCircle className="w-5 h-5 text-green-600" data-id="element-296" />
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-297">
                    Tâches validées ({approvedTasks.length})
                  </h2>
                </div>
                <div className="space-y-3" data-id="element-298">
                  {approvedTasks.map(task => <div key={task.id} className="p-3 bg-green-50 border border-green-200 rounded-lg" data-id="element-299">
                      <div className="flex items-start justify-between gap-2 mb-1" data-id="element-300">
                        <h4 className="font-semibold text-gray-900 text-sm" data-id="element-301">
                          {task.title}
                        </h4>
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" data-id="element-302" />
                      </div>
                      <p className="text-xs text-gray-600 mb-2" data-id="element-303">
                        {task.description}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500" data-id="element-304">
                        <span data-id="element-305">Validé par {task.validatedBy}</span>
                        <span data-id="element-306">
                          {task.images.length} photo
                          {task.images.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      {task.validationComment && <p className="text-xs text-green-700 mt-2 italic" data-id="element-307">
                          "{task.validationComment}"
                        </p>}
                    </div>)}
                </div>
              </Card>}

            {/* Photos */}
            <div data-id="element-308">
              <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-309">
                Photos ({allImages.length})
              </h2>
              <ImageGallery images={allImages} data-id="element-310" />
            </div>

            {/* Documents */}
            {chantier.documents.length > 0 && <Card className="p-4 sm:p-6" data-id="element-311">
                <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-312">
                  Documents ({chantier.documents.length})
                </h2>
                <div className="space-y-2" data-id="element-313">
                  {chantier.documents.map(doc => <div key={doc.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-314">
                      <FileText size={18} className="text-gray-500 flex-shrink-0" data-id="element-315" />
                      <div className="flex-1 min-w-0" data-id="element-316">
                        <p className="text-sm font-medium text-gray-900 truncate" data-id="element-317">
                          {doc.name}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-318">
                          {docTypeLabels[doc.type]} ·{' '}
                          {new Date(doc.uploadedAt).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>)}
                </div>
              </Card>}
          </div>

          {/* Sidebar */}
          <div className="lg:w-80 space-y-6" data-id="element-319">
            {/* Chef d'équipe */}
            {chef && <Card hover={false} className="p-5 bg-gradient-to-br from-teal-50 to-white border-teal-200" data-id="element-320">
                <div className="flex items-center gap-2 mb-4" data-id="element-321">
                  <UserCheck className="w-5 h-5 text-teal-600" data-id="element-322" />
                  <h3 className="text-sm font-semibold text-teal-900 uppercase tracking-wider" data-id="element-323">
                    Chef d'Équipe
                  </h3>
                </div>
                <div className="flex items-center gap-3" data-id="element-324">
                  <img src={chef.avatar} alt={chef.name} className="w-12 h-12 rounded-full ring-2 ring-teal-200" data-id="element-325" />
                  <div data-id="element-326">
                    <p className="font-bold text-gray-900" data-id="element-327">{chef.name}</p>
                    <p className="text-sm text-gray-600" data-id="element-328">Responsable</p>
                  </div>
                </div>
              </Card>}

            <Card hover={false} className="p-5 space-y-6" data-id="element-329">
              {/* Planning */}
              <div data-id="element-330">
                <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider" data-id="element-331">
                  Planning
                </h3>
                <div className="space-y-3" data-id="element-332">
                  <div className="flex items-center text-sm" data-id="element-333">
                    <Calendar className="w-5 h-5 text-gray-400 mr-3" data-id="element-334" />
                    <div data-id="element-335">
                      <p className="text-gray-900 font-medium" data-id="element-336">Début</p>
                      <p className="text-gray-500" data-id="element-337">
                        {new Date(chantier.startDate).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center text-sm" data-id="element-338">
                    <Calendar className="w-5 h-5 text-gray-400 mr-3" data-id="element-339" />
                    <div data-id="element-340">
                      <p className="text-gray-900 font-medium" data-id="element-341">Fin estimée</p>
                      <p className="text-gray-500" data-id="element-342">
                        {new Date(chantier.endDate).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Team */}
              <div className="pt-6 border-t border-gray-100" data-id="element-343">
                <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider" data-id="element-344">
                  Équipe sur site
                </h3>
                <div className="space-y-3" data-id="element-345">
                  {chantier.team.map(member => <div key={member.id} className="flex items-center gap-3" data-id="element-346">
                      <img src={member.avatar} alt={member.name} className="w-8 h-8 rounded-full" data-id="element-347" />
                      <div data-id="element-348">
                        <p className="text-sm font-medium text-gray-900" data-id="element-349">
                          {member.name}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-350">Technicien</p>
                      </div>
                    </div>)}
                  {chantier.team.length === 0 && <p className="text-sm text-gray-400" data-id="element-351">
                      Aucun technicien assigné
                    </p>}
                </div>
              </div>

              {/* Progress */}
              <div className="pt-6 border-t border-gray-100" data-id="element-352">
                <h3 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider" data-id="element-353">
                  Progression
                </h3>
                <div className="flex items-end justify-between mb-2" data-id="element-354">
                  <span className="text-2xl font-bold text-gray-900" data-id="element-355">
                    {chantier.progress}%
                  </span>
                  <span className="text-sm text-gray-500 mb-1" data-id="element-356">complété</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-357">
                  <motion.div className="bg-[#1DBAB4] h-2 rounded-full" initial={{
                  width: 0
                }} animate={{
                  width: `${chantier.progress}%`
                }} transition={{
                  duration: 1,
                  ease: 'easeOut'
                }} data-id="element-358" />
                </div>
                <p className="text-xs text-gray-500 mt-2" data-id="element-359">
                  {approvedTasks.length} / {tasks.length} tâches validées
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}